import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Save, Sliders, Eye, EyeOff } from "lucide-react";

const GAME_TYPES = ["tournament", "league", "playoff", "exhibition", "scrimmage", "showcase"];
const ENTRY_SOURCES = ["manual", "live", "import"];

const numMap = (obj) => Object.entries(obj || {});

export default function AdminInGameRankings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/ingame-rankings/config").then(({ data }) => setCfg(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  if (!cfg) return <AdminLayout><div className="p-8 label-overline text-slate-500">Loading…</div></AdminLayout>;

  const setNested = (group, key, val) => setCfg((c) => ({ ...c, [group]: { ...c[group], [key]: val } }));
  const setTop = (key, val) => setCfg((c) => ({ ...c, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        min_samples: mapNums(cfg.min_samples), min_comparison_group: Number(cfg.min_comparison_group),
        hitting_weights: mapNums(cfg.hitting_weights), pitching_weights: mapNums(cfg.pitching_weights),
        fielding_weights: mapNums(cfg.fielding_weights), overall_weights: cfg.overall_weights,
        confidence_thresholds: cfg.confidence_thresholds,
        eligible_game_types: cfg.eligible_game_types, eligible_entry_sources: cfg.eligible_entry_sources,
        public_visible: cfg.public_visible, methodology_version: cfg.methodology_version,
      };
      const { data } = await api.put("/admin/ingame-rankings/config", payload);
      toast.success(`Saved · ${data.recompute?.players ?? 0} players recomputed (v${data.recompute?.methodology_version})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const recompute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ingame-rankings/recompute");
      toast.success(`Full recompute done · ${data.players} players, ${data.ranked} ranked`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const toggleList = (key, val) => {
    const cur = cfg[key] || [];
    setTop(key, cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="admin-ingame-rankings">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 label-overline text-[#E63946]"><Sliders className="w-4 h-4" /> In-Game Rankings</div>
            <h1 className="font-display text-3xl uppercase tracking-tight">Performance Score Methodology</h1>
            <p className="text-sm text-slate-500 mt-1">Approved CBR game data only. Separate from CBR Overall Rating. Methodology v{cfg.methodology_version}.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="ig-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider disabled:opacity-50"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save & Recompute"}</button>
            <button data-testid="ig-recompute" onClick={recompute} disabled={busy} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Full Recompute</button>
          </div>
        </div>

        <Card title="Public Visibility">
          <button data-testid="ig-toggle-public" onClick={() => setTop("public_visible", !cfg.public_visible)} className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-semibold ${cfg.public_visible ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
            {cfg.public_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />} {cfg.public_visible ? "Public leaderboards & scores VISIBLE" : "Hidden from public"}
          </button>
        </Card>

        <Card title="Minimum Samples (qualification)">
          <Grid>{numMap(cfg.min_samples).map(([k, v]) => (
            <NumField key={k} label={k.replace(/_/g, " ")} value={v} onChange={(x) => setNested("min_samples", k, x)} />
          ))}</Grid>
          <NumField label="Minimum comparison group" value={cfg.min_comparison_group} onChange={(x) => setTop("min_comparison_group", x)} />
        </Card>

        <Card title="Hitting Component Weights"><Grid>{numMap(cfg.hitting_weights).map(([k, v]) => <NumField key={k} step="0.05" label={k.replace(/_/g, " ")} value={v} onChange={(x) => setNested("hitting_weights", k, x)} />)}</Grid></Card>
        <Card title="Pitching Component Weights"><Grid>{numMap(cfg.pitching_weights).map(([k, v]) => <NumField key={k} step="0.05" label={k.replace(/_/g, " ")} value={v} onChange={(x) => setNested("pitching_weights", k, x)} />)}</Grid></Card>
        <Card title="Fielding Component Weights"><Grid>{numMap(cfg.fielding_weights).map(([k, v]) => <NumField key={k} step="0.05" label={k.replace(/_/g, " ")} value={v} onChange={(x) => setNested("fielding_weights", k, x)} />)}</Grid></Card>

        <Card title="Overall Score Weights by Player Type">
          {Object.entries(cfg.overall_weights || {}).map(([ptype, w]) => (
            <div key={ptype} className="mb-3">
              <div className="label-overline mb-1">{ptype.replace(/_/g, " ")}</div>
              <Grid>{Object.entries(w).map(([comp, val]) => (
                <NumField key={comp} step="0.05" label={comp} value={val} onChange={(x) => setCfg((c) => ({ ...c, overall_weights: { ...c.overall_weights, [ptype]: { ...c.overall_weights[ptype], [comp]: Number(x) } } }))} />
              ))}</Grid>
            </div>
          ))}
        </Card>

        <Card title="Confidence Thresholds">
          {Object.entries(cfg.confidence_thresholds || {}).map(([lvl, t]) => (
            <div key={lvl} className="mb-3">
              <div className="label-overline mb-1">{lvl}</div>
              <Grid>{Object.entries(t).map(([k, v]) => (
                <NumField key={k} label={k} value={v} onChange={(x) => setCfg((c) => ({ ...c, confidence_thresholds: { ...c.confidence_thresholds, [lvl]: { ...c.confidence_thresholds[lvl], [k]: Number(x) } } }))} />
              ))}</Grid>
            </div>
          ))}
        </Card>

        <Card title="Eligible Game Types">
          <div className="flex flex-wrap gap-2">{GAME_TYPES.map((g) => (
            <Chip key={g} active={(cfg.eligible_game_types || []).includes(g)} onClick={() => toggleList("eligible_game_types", g)}>{g}</Chip>
          ))}</div>
        </Card>
        <Card title="Eligible Entry Sources">
          <div className="flex flex-wrap gap-2">{ENTRY_SOURCES.map((g) => (
            <Chip key={g} active={(cfg.eligible_entry_sources || []).includes(g)} onClick={() => toggleList("eligible_entry_sources", g)}>{g}</Chip>
          ))}</div>
          <p className="text-xs text-slate-400 mt-2">Approved-only enforcement is locked ON for all public views.</p>
        </Card>
      </div>

      <PlayerEligibilityCheck />
      <RankingDiagnostics />
    </AdminLayout>
  );
}

const mapNums = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k, Number(v)]));

const Card = ({ title, children }) => (
  <div className="border border-slate-200 rounded-sm p-5 bg-white">
    <div className="label-overline text-[#0A192F] mb-3">{title}</div>{children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{children}</div>;
const NumField = ({ label, value, onChange, step = "1" }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
    <input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm font-data" />
  </label>
);
const Chip = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${active ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-500 border-slate-300"}`}>{children}</button>
);

function PlayerEligibilityCheck() {
  const [q, setQ] = useState("");
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    if (!q.trim()) return;
    setBusy(true); setR(null);
    try { const { data } = await api.get("/admin/ingame-rankings/player-check", { params: { q } }); setR(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const VERDICT = {
    scored: ["Scored ✓", "bg-emerald-100 text-emerald-800"],
    no_stats: ["No stats entered", "bg-slate-100 text-slate-600"],
    all_lines_excluded: ["All lines excluded", "bg-rose-100 text-rose-700"],
    eligible_but_below_sample: ["Below min sample", "bg-amber-100 text-amber-800"],
    eligible_no_row: ["Needs recompute", "bg-amber-100 text-amber-800"],
  };
  return (
    <div className="mt-8" data-testid="player-eligibility-check">
      <h2 className="font-display text-2xl uppercase tracking-tight">Check a Player</h2>
      <p className="text-xs text-slate-500 mt-1">See exactly why any player is or isn't getting an In-Game Performance score — inspects every stat line, including unapproved/draft ones a recompute skips.</p>
      <div className="flex gap-2 mt-3 max-w-lg">
        <input data-testid="pcheck-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && check()}
          placeholder="Player name, slug or id (e.g. Wes Crozier)" className="flex-1 border border-slate-300 rounded-sm px-3 py-2 text-sm" />
        <button data-testid="pcheck-run" onClick={check} disabled={busy} className="bg-[#0A192F] text-white px-4 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider disabled:opacity-50">{busy ? "Checking…" : "Check"}</button>
      </div>
      {r && (
        <div className="mt-4 border border-slate-200 rounded-sm bg-white p-4" data-testid="pcheck-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-semibold text-[#0A192F]">{r.player.name} <span className="text-slate-400 font-normal text-xs">· {r.player.primary_position || "—"}{r.player.public_visible === false ? " · HIDDEN (public_visible off)" : ""}</span></div>
            <span className={`text-[10px] px-2 py-1 rounded-sm font-bold uppercase ${(VERDICT[r.verdict] || ["", "bg-slate-100"])[1]}`}>{(VERDICT[r.verdict] || [r.verdict])[0]}</span>
          </div>
          <div className="text-sm mt-2 flex flex-wrap gap-x-5 gap-y-1 font-data">
            <span>Lines: <b>{r.totals.eligible_lines}</b>/{r.totals.lines} eligible</span>
            <span>PA: <b className={r.totals.pa < r.totals.thr_pa ? "text-amber-600" : "text-emerald-700"}>{r.totals.pa}</b>/{r.totals.thr_pa} min</span>
            <span>IP-outs: <b>{r.totals.ip_outs}</b></span>
            <span>Score row: <b>{r.has_score_row ? `yes (${r.overall_score ?? "—"})` : "none"}</b></span>
          </div>
          {r.fix && <div className="mt-3 text-sm bg-amber-50 border-l-4 border-amber-400 text-amber-900 p-3 rounded-sm">{r.fix}</div>}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs min-w-max">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left label-overline">
                <th className="p-2">Date</th><th className="p-2">Opponent</th><th className="p-2">Type</th><th className="p-2">Entry</th>
                <th className="p-2">Line</th><th className="p-2">Game</th><th className="p-2 text-center">PA</th><th className="p-2 text-center">AB</th><th className="p-2">Eligible?</th>
              </tr></thead>
              <tbody>
                {r.lines.map((l, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="p-2 font-data">{(l.date || "").slice(0, 10) || "—"}</td>
                    <td className="p-2">{l.opponent || "—"}</td>
                    <td className="p-2 text-slate-500">{l.record_type === "player_only_entry" ? "player-only" : "team game"}</td>
                    <td className="p-2 text-slate-500">{l.entry_source || "—"}</td>
                    <td className="p-2"><span className={l.line_approval === "approved" ? "text-emerald-700" : "text-rose-600 font-semibold"}>{l.line_approval || "draft"}</span></td>
                    <td className="p-2"><span className={l.game_status === "final" && l.game_approval === "approved" ? "text-emerald-700" : "text-rose-600"}>{l.game_status || "?"}/{l.game_approval || "?"}</span></td>
                    <td className="p-2 text-center font-data">{l.pa}</td>
                    <td className="p-2 text-center font-data">{l.ab}</td>
                    <td className="p-2">{l.eligible ? <span className="text-emerald-700 font-semibold">✓</span> : <span className="text-rose-600" title={(l.exclusion_reasons || []).join("; ")}>✗ {(l.exclusion_reasons || [])[0]}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RankingDiagnostics() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [needs, setNeeds] = useState(null);
  const loadDiag = () => { setOpen(true); api.get("/admin/ingame-rankings/diagnostics").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))); api.get("/admin/participation-context/needs-review").then((r) => setNeeds(r.data)).catch(() => {}); };
  const backfill = async () => {
    try { const { data } = await api.post("/admin/participation-context/backfill"); toast.success(`Backfill: ${data.cbr_nation_updated} CBR Nation · ${data.regular_team_updated} regular · ${data.needs_review_flagged} need review`); loadDiag(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-8" data-testid="ranking-diagnostics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl uppercase tracking-tight">Player Ranking Diagnostics</h2>
        <div className="flex gap-2">
          <button data-testid="run-backfill" onClick={backfill} className="border border-slate-300 px-4 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider">Run Affiliation Backfill</button>
          <button data-testid="load-diagnostics" onClick={loadDiag} className="bg-[#0A192F] text-white px-4 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider">Load Diagnostics</button>
        </div>
      </div>
      {open && d && (
        <div className="mt-4">
          <p className="text-xs text-slate-500 mb-2">Thresholds — Hitting: {d.thresholds.hitting_pa} PA · Pitching: {d.thresholds.pitching_ip_outs} IP-outs · Fielding: {d.thresholds.fielding_chances} chances · Min comparison group: {d.thresholds.min_comparison_group}. {needs && <span className="text-amber-600 font-semibold">Needs affiliation review: {needs.count}</span>}</p>
          <div className="border border-slate-200 rounded-sm overflow-x-auto bg-white">
            <table className="w-full text-sm min-w-max">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left label-overline">
                <th className="p-2">Player</th><th className="p-2 text-center">G</th><th className="p-2 text-center">PA</th><th className="p-2 text-center">IP-outs</th>
                <th className="p-2">Qualified</th><th className="p-2">Excluded</th><th className="p-2 text-center">Rank</th><th className="p-2 text-center">Group</th><th className="p-2 text-center">Status</th><th className="p-2">Last Recalc</th>
              </tr></thead>
              <tbody>
                {d.players.map((p) => (
                  <tr key={p.player_id} data-testid={`diag-${p.player_id}`} className="border-b border-slate-100 last:border-0">
                    <td className="p-2 font-semibold">{p.name || p.slug}</td>
                    <td className="p-2 text-center font-data">{p.approved_games}</td>
                    <td className="p-2 text-center font-data">{p.pa}</td>
                    <td className="p-2 text-center font-data">{p.ip_outs}</td>
                    <td className="p-2 text-emerald-700 text-xs">{(p.qualified_categories || []).join(", ") || "—"}</td>
                    <td className="p-2 text-slate-500 text-xs">{(p.excluded_categories || []).join("; ") || "—"}</td>
                    <td className="p-2 text-center font-data">{p.national_rank || "—"}</td>
                    <td className="p-2 text-center font-data">{p.comparison_group}</td>
                    <td className="p-2 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase ${p.rank_status === "official" ? "bg-emerald-100 text-emerald-800" : p.rank_status === "provisional" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`}>{p.rank_status}</span></td>
                    <td className="p-2 text-[10px] text-slate-400">{(p.last_recalculated || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
